import { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const inputCls =
  "focus-visible:ring-2 focus-visible:ring-[#F97316]/30 focus-visible:border-[#F97316]";

const EMAIL_RE = /^\S+@\S+\.\S+$/;

// K&G sales agents multi-select (chips) with an inline "add new agent" form.
// Every selected agent is CC'd on RFPs and customer quotes.
export const AgentSelector = ({ salesAgents, setSalesAgents }) => {
  const [agents, setAgents] = useState([]);
  const [addingAgent, setAddingAgent] = useState(false);
  const [newAgent, setNewAgent] = useState({ name: "", email: "" });
  const [savingAgent, setSavingAgent] = useState(false);

  useEffect(() => {
    api.get("/sales-agents").then(({ data }) => setAgents(data)).catch(() => {});
  }, []);

  const addSelection = (a) => {
    if (!salesAgents.some((x) => x.email === a.email)) {
      setSalesAgents([...salesAgents, { name: a.name, email: a.email }]);
    }
  };

  const onAgentPick = (v) => {
    if (v === "__add__") {
      setAddingAgent(true);
      return;
    }
    const a = agents.find((x) => x.email === v);
    if (a) addSelection(a);
  };

  const removeSelection = (email) =>
    setSalesAgents(salesAgents.filter((x) => x.email !== email));

  const saveNewAgent = async () => {
    if (!newAgent.name.trim() || !EMAIL_RE.test(newAgent.email.trim())) {
      toast.error("Enter the agent's name and a valid email");
      return;
    }
    setSavingAgent(true);
    try {
      const { data } = await api.post("/sales-agents", {
        name: newAgent.name.trim(),
        email: newAgent.email.trim(),
      });
      setAgents((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      addSelection(data);
      setNewAgent({ name: "", email: "" });
      setAddingAgent(false);
      toast.success(`Agent ${data.name} added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add agent");
    } finally {
      setSavingAgent(false);
    }
  };

  return (
    <div className="pt-2 border-t border-border">
      <Label className="text-xs font-bold uppercase tracking-[0.1em] text-[#57534E]">
        K&amp;G Sales Agents
      </Label>
      <p className="text-xs text-[#57534E] mt-0.5">
        Everyone selected receives a copy of the quote when the RFP is emailed.
      </p>
      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
        <div>
          <Label className="text-[10px] font-bold uppercase text-[#57534E]">Add Sales Agent</Label>
          {/* value stays "" so onValueChange fires even when re-picking */}
          <Select value="" onValueChange={onAgentPick}>
            <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="sales-agent-select">
              <SelectValue
                placeholder={
                  salesAgents.length
                    ? `${salesAgents.length} agent${salesAgents.length > 1 ? "s" : ""} selected — add more…`
                    : "Select agent…"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {agents.map((a) => (
                <SelectItem key={a.id} value={a.email} data-testid={`sales-agent-option-${a.id}`}>
                  {salesAgents.some((x) => x.email === a.email) ? "✓ " : ""}
                  {a.name} — {a.email}
                </SelectItem>
              ))}
              <SelectItem value="__add__" data-testid="sales-agent-add-option">
                + Add new agent…
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        {salesAgents.length > 0 && (
          <div className="md:pt-5">
            <div className="flex flex-wrap gap-1.5" data-testid="sales-agent-selected">
              {salesAgents.map((a) => (
                <Badge
                  key={a.email}
                  variant="secondary"
                  className="bg-[#FFF7ED] text-[#EA580C] border border-[#F97316]/30 gap-1"
                  data-testid={`sales-agent-chip-${a.email}`}
                >
                  {a.name || a.email}
                  <button onClick={() => removeSelection(a.email)} className="hover:text-[#1C1917]">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <p className="text-[11px] text-[#57534E] mt-1">
              CC on RFPs &amp; quotes:{" "}
              <span className="font-medium text-[#1C1917]">{salesAgents.map((a) => a.email).join(", ")}</span>
            </p>
          </div>
        )}
      </div>
      {addingAgent && (
        <div className="mt-3 rounded-md border border-[#F97316]/40 bg-[#FFF7ED] p-3">
          <Label className="text-[10px] font-bold uppercase text-[#EA580C]">New Agent</Label>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              className={inputCls}
              placeholder="Agent name"
              value={newAgent.name}
              onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
              data-testid="new-agent-name"
            />
            <Input
              type="email"
              className={inputCls}
              placeholder="agent@kgmowers.com"
              value={newAgent.email}
              onChange={(e) => setNewAgent({ ...newAgent, email: e.target.value })}
              data-testid="new-agent-email"
            />
          </div>
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              onClick={saveNewAgent}
              disabled={savingAgent}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold"
              data-testid="new-agent-save"
            >
              {savingAgent ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
              Add Agent
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAddingAgent(false)} data-testid="new-agent-cancel">
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
